import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { ICampaign } from '../redux/buzzbridge/types';
import type { ICampaignAppInstallPayoutBreakdown } from '../types/global/campaignFeeBreakdown.types';
import { hasActiveCreatorThspianPlanSelector } from '../pages/EsSettings/selector';
import { resolveCreatorPayoutForViewer } from '../utils/creatorPayoutBreakdown';
import {
    estimateAppInstallEarnings,
    formatCpaDollarsFromCents,
    type IAppInstallEarningsEstimate,
} from '../utils/circuulCpaRates';

function mapServerAppInstallPayout(server: ICampaignAppInstallPayoutBreakdown): IAppInstallEarningsEstimate {
    const cpiUnsubscribedCents = Number(server.cpi_unsubscribed_cents) || 0;
    const cpiSubscribedCents = Number(server.cpi_subscribed_cents) || 0;
    const cpiAppliedCents = Number(server.cpi_applied_cents) || cpiUnsubscribedCents;
    const earningsUnsubscribedCents = Number(server.earnings_unsubscribed_cents) || 0;
    const earningsSubscribedCents = Number(server.earnings_subscribed_cents) || 0;
    const earningsAppliedCents =
        Number(server.earnings_applied_cents) || cpiAppliedCents * (server.benchmark_installs || 100);
    return {
        tier: (server.tier as IAppInstallEarningsEstimate['tier']) || 'micro',
        benchmarkInstalls: Number(server.benchmark_installs) || 100,
        creatorSubscribed: Boolean(server.viewer_has_active_subscription),
        cpiUnsubscribedCents,
        cpiSubscribedCents,
        cpiAppliedCents,
        earningsUnsubscribedCents,
        earningsSubscribedCents,
        earningsAppliedCents,
        extraIfSubscribedCents:
            Number(server.extra_if_subscribed_cents) ||
            Math.max(0, earningsSubscribedCents - earningsUnsubscribedCents),
    };
}

export function useCreatorCampaignPayoutDisplay(campaign: ICampaign) {
    const clientHasActiveSubscription = useSelector(hasActiveCreatorThspianPlanSelector);
    const breakdown = campaign.fee_breakdown?.creator_payout;
    const serverAppInstall = campaign.fee_breakdown?.app_install_payout;
    const isAppInstallCpa = Boolean(campaign.app_install?.enabled || serverAppInstall);
    const influencerTier = campaign.influencer_tier;
    const appInstallKind = campaign.app_install?.kind;
    const basePaymentPerCreatorCents = Number(campaign.base_payment_per_creator_cents) || 0;

    return useMemo(() => {
        if (isAppInstallCpa) {
            const appInstallEstimate = serverAppInstall
                ? mapServerAppInstallPayout(serverAppInstall)
                : estimateAppInstallEarnings({
                      influencerTier,
                      creatorSubscribed: clientHasActiveSubscription,
                  });
            const kindLabel =
                (serverAppInstall?.kind_label || serverAppInstall?.kind) === 'visit'
                    ? ('visit' as const)
                    : appInstallKind === 'web_visit'
                    ? ('visit' as const)
                    : ('install' as const);

            const basePayCents = basePaymentPerCreatorCents;
            const cpaEarningsCents = serverAppInstall
                ? Number(serverAppInstall.earnings_applied_cents) || 0
                : appInstallEstimate.earningsAppliedCents;
            const basePayFormatted = basePayCents > 0 ? formatCpaDollarsFromCents(basePayCents) : null;
            const additionalCents = Number(cpaEarningsCents) || 0;
            const estimatedCpaFormatted = formatCpaDollarsFromCents(additionalCents);
            const estimatedTotalFormatted = formatCpaDollarsFromCents(basePayCents + additionalCents);

            return {
                isAppInstallCpa: true as const,
                appInstallEstimate,
                appInstallKindLabel: kindLabel,
                breakdown: null,
                resolved: null,
                basePayCents,
                basePayFormatted,
                netAmountFormatted: estimatedTotalFormatted,
                estimatedCpaFormatted,
                estimatedTotalFormatted,
                clientHasActiveSubscription:
                    serverAppInstall?.viewer_has_active_subscription ?? clientHasActiveSubscription,
            };
        }

        if (!breakdown) {
            return {
                isAppInstallCpa: false as const,
                appInstallEstimate: null as IAppInstallEarningsEstimate | null,
                appInstallKindLabel: null as 'install' | 'visit' | null,
                breakdown: null,
                resolved: null,
                netAmountFormatted: null as string | null,
                estimatedCpaFormatted: null as string | null,
                estimatedTotalFormatted: null as string | null,
                basePayFormatted: null as string | null,
                basePayCents: 0,
                clientHasActiveSubscription,
            };
        }

        const resolved = resolveCreatorPayoutForViewer(breakdown, clientHasActiveSubscription);
        const netUsd = resolved.applied?.net_amount_usd;
        const netAmountFormatted =
            netUsd != null && Number.isFinite(netUsd)
                ? `$${netUsd.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  })}`
                : null;

        return {
            isAppInstallCpa: false as const,
            appInstallEstimate: null as IAppInstallEarningsEstimate | null,
            appInstallKindLabel: null as 'install' | 'visit' | null,
            breakdown,
            resolved,
            netAmountFormatted,
            estimatedCpaFormatted: null as string | null,
            estimatedTotalFormatted: null as string | null,
            basePayFormatted: null as string | null,
            basePayCents: 0,
            clientHasActiveSubscription,
        };
    }, [
        appInstallKind,
        basePaymentPerCreatorCents,
        breakdown,
        clientHasActiveSubscription,
        influencerTier,
        isAppInstallCpa,
        serverAppInstall,
    ]);
}
